import {
  IEventItem,
  IPermissionItem,
  IPenaltyItem,
  ISettingsItem,
  ISessionSettings,
  ITaskItem,
} from "../ts/Definitions";
import { ItemTypes } from "../ts/enums";
import { isAvailableItem } from "../utilities/utils";

/**
 * CategoryHelper - Centralized category filtering and management
 * 
 * Responsibilities:
 * - Extract categories from items
 * - Filter items by category
 * - Update item visibility based on categories
 * - Calculate category relationships
 */
export class CategoryHelper {
  /**
   * Get all categories for a specific task, including nested item categories
   */
  public getCategoriesForTask(
    task: ITaskItem | undefined,
    getTaskCallback: (key: string) => ITaskItem | undefined
  ): string[] {
    if (!task || !isAvailableItem(task)) return [];

    let cats: string[] = [];

    if (task.categories) {
      cats = [...task.categories];
    }

    if (task.itemsUsed) {
      task.itemsUsed.forEach((itemGroup: string[]) => {
        itemGroup.forEach((itemKey: string) => {
          const itemCats = this.getCategoriesForTask(getTaskCallback(itemKey), getTaskCallback);
          itemCats.forEach((cat) => {
            if (cats.indexOf(cat) === -1) cats.push(cat);
          });
        });
      });
    }

    return cats.sort();
  }

  /**
   * Get all categories for a specific penalty, including nested item categories
   */
  public getCategoriesForPenalty(
    penalty: IPenaltyItem | undefined,
    getTaskCallback: (key: string) => ITaskItem | undefined
  ): string[] {
    if (!penalty || !isAvailableItem(penalty)) return [];

    let cats: string[] = [];

    if (penalty.categories) {
      cats = [...penalty.categories];
    }

    if (penalty.itemsUsed) {
      penalty.itemsUsed.forEach((itemGroup: string[]) => {
        itemGroup.forEach((itemKey: string) => {
          const itemCats = this.getCategoriesForTask(getTaskCallback(itemKey), getTaskCallback);
          itemCats.forEach((cat) => {
            if (cats.indexOf(cat) === -1) cats.push(cat);
          });
        });
      });
    }

    return cats.sort();
  }

  /**
   * Get all categories for a specific event
   */
  public getCategoriesForEvent(
    event: IEventItem | undefined,
    getTaskCallback: (key: string) => ITaskItem | undefined
  ): string[] {
    if (!event || !isAvailableItem(event)) return [];

    let cats: string[] = [];
    cats = event.categories ?? [];

    if (event.taskCategories) {
      event.taskCategories.forEach((c: string) => {
        if (cats.indexOf(c) === -1) cats.push(c);
      });
    }

    if (event.taskKeys) {
      event.taskKeys.forEach((taskKey: string) => {
        const taskCats = this.getCategoriesForTask(getTaskCallback(taskKey), getTaskCallback);
        if (taskCats) {
          taskCats.forEach((cat) => {
            if (cats.indexOf(cat) === -1) cats.push(cat);
          });
        }
      });
    }

    return cats.sort();
  }

  /**
   * Get all categories for a specific permission
   */
  public getCategoriesForPermission(
    permission: IPermissionItem | undefined,
    getTaskCallback: (key: string) => ITaskItem | undefined,
    getPenaltyCallback: (key: string) => IPenaltyItem | undefined
  ): string[] {
    if (!permission || !isAvailableItem(permission)) return [];

    let cats: string[] = [];
    cats = permission.categories ?? [];

    if (permission.taskCategories) {
      permission.taskCategories.forEach((c: string) => {
        if (cats.indexOf(c) === -1) cats.push(c);
      });
    }

    if (permission.taskKeys) {
      permission.taskKeys.forEach((taskKey: string) => {
        const taskCats = this.getCategoriesForTask(getTaskCallback(taskKey), getTaskCallback);
        if (taskCats) {
          taskCats.forEach((cat) => {
            if (cats.indexOf(cat) === -1) cats.push(cat);
          });
        }
      });
    }

    if (permission.penalties) {
      permission.penalties.forEach((penaltyKey: string) => {
        const penaltyCats = this.getCategoriesForPenalty(getPenaltyCallback(penaltyKey), getTaskCallback);
        if (penaltyCats) {
          penaltyCats.forEach((cat) => {
            if (cats.indexOf(cat) === -1) cats.push(cat);
          });
        }
      });
    }

    return cats.sort();
  }

  /**
   * Get all categories from items based on type
   */
  public getCategoriesForItem(
    item: ISettingsItem,
    getTaskCallback: (key: string) => ITaskItem | undefined,
    getPenaltyCallback: (key: string) => IPenaltyItem | undefined,
    getEventCallback: (key: string) => IEventItem | undefined,
    getPermissionCallback: (key: string) => IPermissionItem | undefined
  ): string[] {
    switch (item.itemType) {
      case ItemTypes.task:
        return this.getCategoriesForTask(item as ITaskItem, getTaskCallback);
      case ItemTypes.penalty:
        return this.getCategoriesForPenalty(item as IPenaltyItem, getTaskCallback);
      case ItemTypes.event:
        return this.getCategoriesForEvent(item as IEventItem, getTaskCallback);
      case ItemTypes.permission:
        return this.getCategoriesForPermission(
          item as IPermissionItem,
          getTaskCallback,
          getPenaltyCallback
        );
      default:
        return [];
    }
  }

  /**
   * Get all unique categories across all item types
   */
  public getAllCategories(
    tasks: ITaskItem[],
    penalties: IPenaltyItem[],
    permissions: IPermissionItem[],
    events: IEventItem[],
    onlyVisible?: boolean
  ): string[] {
    const catsArr: string[] = [];

    const addCategories = (items: (ITaskItem | IPenaltyItem | IPermissionItem | IEventItem)[]) => {
      items
        .filter((item) => !onlyVisible || isAvailableItem(item))
        .forEach((item) => {
          item.categories?.forEach((c: string) => {
            if (c && catsArr.indexOf(c) === -1) {
              catsArr.push(c);
            }
          });
        });
    };

    addCategories(tasks);
    addCategories(penalties);
    addCategories(permissions);
    addCategories(events);

    return catsArr.sort();
  }

  /**
   * Get categories that are selected in task credits
   */
  public getSelectedCategories(credits: ITaskItem[] | undefined): string[] {
    if (!credits) return [];
    const cats: string[] = [];
    credits.forEach((credit) => {
      if (credit && isAvailableItem(credit) && credit.categories) {
        credit.categories.forEach((cat: string) => {
          if (cats.indexOf(cat) === -1) cats.push(cat);
        });
      }
    });
    return cats.sort();
  }

  /**
   * Get hint categories (selected but not in favorites)
   */
  public getHintProfileCategories(
    favCategories: string[] | undefined,
    settings: ISessionSettings
  ): string[] {
    const selectedCats = this.getSelectedCategories(
      settings.creditSettings.credits
    );
    const favCats = favCategories || [];
    return selectedCats.filter((c) => favCats?.indexOf(c) === -1);
  }

  /**
   * Get categories not in favorites
   */
  public getMissingProfileCategories(
    favCategories: string[],
    allCategories: string[]
  ): string[] {
    return allCategories.filter((c) => !favCategories?.includes(c));
  }

  /**
   * Update item visibility based on included categories
   */
  public updateWithCategories(
    tasks: ITaskItem[],
    events: IEventItem[],
    permissions: IPermissionItem[],
    penalties: IPenaltyItem[],
    categories: string[] | undefined,
    getCategoriesCallback: {
      forTask: (key: string) => string[];
      forEvent: (key: string) => string[];
      forPermission: (key: string) => string[];
      forPenalty: (key: string) => string[];
    }
  ): void {
    tasks.forEach((task) => {
      const cats = getCategoriesCallback.forTask(task.key);
      task.hidden =
        categories &&
        categories.length > 0 &&
        cats &&
        cats.length > 0 &&
        cats.filter((cr) => categories && categories.includes(cr)).length === 0;
    });

    events.forEach((event) => {
      const cats = getCategoriesCallback.forEvent(event.key);
      event.hidden =
        categories &&
        categories.length > 0 &&
        cats.length > 0 &&
        cats.filter((cr) => categories && categories.includes(cr)).length !==
        categories.length;
    });

    permissions.forEach((permission) => {
      const cats = getCategoriesCallback.forPermission(permission.key);
      permission.hidden =
        categories &&
        categories.length > 0 &&
        cats.length > 0 &&
        cats.filter((cr) => categories && categories.includes(cr)).length !==
        categories.length;
    });

    penalties.forEach((penalty) => {
      const cats = getCategoriesCallback.forPenalty(penalty.key);
      penalty.hidden =
        categories &&
        categories.length > 0 &&
        cats.length > 0 &&
        cats.filter((cr) => categories && categories.includes(cr)).length !==
        categories.length;
    });
  }

  /**
   * Disable items based on omitted categories
   */
  public disableLimits(
    settings: ISessionSettings,
    omitCategories: string[] | undefined,
    getCategoriesCallback: {
      forTask: (key: string) => string[];
      forEvent: (key: string) => string[];
      forPermission: (key: string) => string[];
      forPenalty: (key: string) => string[];
    }
  ): void {
    settings.creditSettings.credits.forEach((task: ITaskItem) => {
      const cats = getCategoriesCallback.forTask(task.key);
      if (isAvailableItem(task) && omitCategories && omitCategories.length > 0)
        task.hidden =
          cats &&
          cats.length > 0 &&
          cats.filter((cr) => omitCategories && omitCategories.includes(cr))
            .length > 0;
    });

    settings.eventsSettings.events.forEach((event: IEventItem) => {
      const cats = getCategoriesCallback.forEvent(event.key);
      if (isAvailableItem(event) && omitCategories && omitCategories.length > 0)
        event.hidden =
          cats &&
          cats.length > 0 &&
          cats.filter((cr) => omitCategories && omitCategories.includes(cr))
            .length > 0;
    });

    settings.permissionSettings.permissions.forEach((permission: IPermissionItem) => {
      const cats = getCategoriesCallback.forPermission(permission.key);
      if (
        isAvailableItem(permission) &&
        omitCategories &&
        omitCategories.length > 0
      )
        permission.hidden =
          cats &&
          cats.length > 0 &&
          cats.filter((cr) => omitCategories && omitCategories.includes(cr))
            .length > 0;
    });

    settings.penaltySettings.penalties.forEach((penalty: IPenaltyItem) => {
      const cats = getCategoriesCallback.forPenalty(penalty.key);
      if (
        isAvailableItem(penalty) &&
        omitCategories &&
        omitCategories.length > 0
      )
        penalty.hidden =
          cats &&
          cats.length > 0 &&
          cats.filter((cr) => omitCategories && omitCategories.includes(cr))
            .length > 0;
    });
  }
}
